import { fields, variant } from 'variant';
import type { ZodError } from 'zod';

export const CliSystemErrorCode = variant({
  FailedToGetDeviceCode: {},
  FailedToGetAuthToken: {},
  FailedToRenewAuthToken: {},
  FailedToRefreshTokenForSite: {},
  FailedToRevokeRefreshToken: fields<{ email: string }>(),
  UnknownFailure: fields<{ error: unknown }>(),
  FailedToFetchPackageJson: {},
  FailedToGetDeveloperApps: {},
  FailedToUpdateTelemetryConfig: {},
  FailedToSyncToRevision: fields<{ revision: string }>(),
  FailedToReadCache: fields<{ path: string }>(),
  FailedToWriteCache: fields<{ path: string }>(),
  UploadApplicationFailed: {},
  FailedToCreateDevCenterApp: {},
  FailedToCreateDevCenterOAuthApp: {},
  FailedToUpdateDevCenterOAuthApp: {},
  FailedToAddPermission: {},
  FailedToGetPlacements: {},
  FailedToGetAppSecret: {},
  FailedToCreateTunnel: {},
  FailedToDeleteTunnel: {},
  FailedToGetBlocksExtensionsData: {},
  FailedToGetBlocksStructure: {},
  FailedToUpdateBlocksAfterSync: {},
  GridAppFailedToUpdateFiles: {},
  FailedToCreateGridApp: fields<{ error?: string }>(),
  InvalidResponseData: {},
  InvalidCreateAppResponse: {},
  InvalidDeployAppResponse: {},
  InvalidGetDeploymentResponse: {},
  InvalidGetDeploymentPipelineDescriptionResponse: {},
  FailedToGetPagesDiff: {},
  InvalidViteBuildOutput: {},
  ViteModuleBundleNotFound: {},
  ViteFileNotFoundInOutput: fields<{ fileName: string }>(),
  FailedToCreateBackendDeployment: {},
  FailedToUpdateBackendDeployment: {},
  FailedToGetBackendDeployment: {},
  FailedToDeployBackend: {},
  FailedToGetUserInfo: {},
  FailedToGetMyAccount: {},
  FailedToCreateDevelopmentSite: {},
  FailedToCreateMetaSiteFromTemplate: {},
  FailedToGetDevelopmentSites: {},
  FailedToGetDevelopmentSitesLimit: {},
  FailedToGetRequiredApps: {},
  FailedToQueryApps: {},
  FailedToGetMarketListing: {},
  FailedToAddRequiredApp: {},
  FailedToQuerySites: {},
  FailedToSetNamespace: {},
  FailedToSetCodeIdentifier: {},
  FailedToGetSite: {},
  FailedToDeploySite: {},
  FailedToDeploySitePreview: {},
  FailedToPublishDeployment: {},
  FailedToGetAppInstallationDetails: fields<{ appId: string }>(),
  TypeErrorInvalidUrl: {},
  FailedToGenerateSelfSignedCertificate: {},
  FailedToResolveViteOrigin: {},
  FailedToReleaseHeadlessProject: {},
  FailedToCreateAppRelease: {},
  FailedToParseHttpErrorResponse: {},
  FailedToUpdateAppRelease: {},
  FailedToCreateComponents: {},
  FailedToGetResolveNpmDependenciesResult: {},
  FailedToResolveNpmDependencies: {},
  FailedToFetchPackageDetails: {},
  FailedToCreateSiteOverride: {},
  FailedToGetDeploymentStatus: {},
  FailedToGetDeploymentPipelineDescription: {},
  FailedToGenerateAppMarketQueryParam: {},
  InvalidDependenciesJsonSchemaError: fields<{
    path: string;
    zodError: ZodError;
  }>(),
  FailedToParseLog: {},
  FailedToConnectToLogsService: {},
  LogsConnectionTimeout: {},
  FailedToFetchAppForVersion: {},
  InvalidSiteOverrideID: {},
  FailedToFindEsbuildOutputFile: {},
  FailedOptimizingWithEsbuild: {},
  FailedReadingEsbuildOutputFile: {},
  InvalidGitUrl: fields<{ url: string }>(),
  TemplatePathNotFound: fields<{ templatePath: string }>(),
  FailedToQueryCliAppTemplates: {},
  FailedToCreateDevCenterAppFromTemplate: {},
  FailedToGetSiteInstalledApps: {},
  FailedToGetSiteInstalledAppInstanceId: {},
  FailedToGetClientSpecMap: {},
  FailedToCreateVeloApp: {},
  FailedToUpdateVeloAppFiles: {},
  FailedToGetFilesFromVelo: {},
  FailedToSyncPagesTypes: {},
  FailedToCreateDraftVersion: {},
  FailedToReleaseDraftVersion: {},
  FailedToDeployDocument: {},
  WaitForEditorInstallationStateFailure: {},
  EditorPendingInstallationCheckTimedout: {},
  FailedToGetMonitoringLoaderScript: fields<{ dsn: string }>(),
  FailedToFetchWixLockFile: {},
  FailedToGetSdkSlotData: {},
  FailedToListComponentByComponentType: {},
  FailedToParseBackofficeExtensionContainer: {},
  FailedToParseBackofficePages: {},
  FailedToPullEnvironment: {},
  FailedToSetEnvironmentVariable: {},
  FailedToRemoveEnvironmentVariable: {},
  FailedToUploadStaticFiles: {},
  FailedCreatingAppProject: {},
  FailedToGetAppProject: {},
  FailedToCreateCodestoreProject: {},
  FailedCreatingAppDeployment: {},
  FailedFinalizingAppDeployment: {},
  FailedCreatingNewFunnelProject: {},
  FailedToFindAstroConfig: {},
  FailedToCreateShareUrl: {},
  FailedToListShareUrls: {},
  FailedToParseNextVersion: fields<{ version: string }>(),
  FailedToDistributeClientManifestUpdate: {},
  FailedToCreateRoutesWithMetaSiteUrl: {},
  FailedToGetUrl: {},
  FailedToUpdateExternalSiteUrl: {},
  FailedToGetGitHubOnboardingStatus: fields<{ projectId: string }>(),
  FailedToInitGitHubRepository: fields<{ projectId: string }>(),
});

export const CliUserErrorCode = variant({
  LatestVersionOfCreateAppRequired: fields<{
    currentVersion: string;
    latestVersion: string;
    updateCommand: string;
  }>(),
  FailedToCreateProjectFolder: fields<{ packageFolder: string }>(),
  EmptyDirectoryRequiredAppGeneration: fields<{ folder: string }>(),
  InvalidNewPackageName: fields<{
    packageName: string;
    validations: string[];
  }>(),
  UnsupportedPackageManager: fields<{
    packageManagerName?: string;
  }>(),
  UnsupportedPackageManagerWorkspaces: fields<{
    packageManagerName: string;
  }>(),
  FailedAddingPackageToWorkspace: {},
  SitePermissionDenied: fields<{ siteId: string }>(),
  AppPermissionDenied: fields<{ appId: string }>(),
  BuiltProjectNeeded: {},
  WixUninstallMissingPackageName: {},
  FailedToReadJson: fields<{ filePath: string }>(),
  FailedToReadFile: fields<{ filePath: string }>(),
  FailedToWriteJson: fields<{ filePath: string }>(),
  FailedToWriteFile: fields<{ filePath: string }>(),
  FailedToCreateDir: fields<{ dirPath: string }>(),
  FailedToRemoveDir: fields<{ dirPath: string }>(),
  InvalidJsonFile: fields<{ filePath: string }>(),
  FailedToWriteWixLock: {},
  FailedToWriteDependenciesMetadata: {},
  InvalidVeloConfigJson: fields<{ configFilename: string }>(),
  DashboardPageConfigNotFound: fields<{ configPath: string }>(),
  EmbeddedScriptConfigNotFound: fields<{ configPath: string }>(),
  NoExtensionsFound: {},
  CustomElementConfigNotFound: fields<{ configPath: string }>(),
  SitePluginConfigNotFound: fields<{ configPath: string }>(),
  BlocksWidgetApiNotFound: fields<{ folderPath: string }>(),
  BlocksWidgetConfigNotFound: fields<{ configPath: string }>(),
  BlocksWidgetPanelConfigNotFound: fields<{ configPath: string }>(),
  NoPagesDirectoryFound: fields<{ srcDir: string }>(),
  DashboardPluginConfigNotFound: fields<{ configPath: string }>(),
  DashboardMenuPluginConfigNotFound: fields<{ configPath: string }>(),
  ServicePluginConfigNotFound: fields<{ configPath: string }>(),
  DashboardModalConfigNotFound: fields<{ configPath: string }>(),
  TSConfigNotFound: fields<{ configPath: string }>(),
  InvalidTSConfigError: fields<{ configPath: string }>(),
  InvalidConfigSchemaError: fields<{
    configPath: string;
    zodError: ZodError;
  }>(),
  InvalidParamsOverrideSchemaError: fields<{
    paramsOverridePath: string;
    zodError: ZodError;
  }>(),
  PermissionDenied: {},
  SiteSelectorTimedout: {},
  FailedToReadProjectFiles: {},
  GridAppFilesMaxLengthExceeded: fields<{
    filePaths: string[];
    maxSizeViolationsData: {
      expectedSize?: number;
      files: Array<{
        fileIndex: number;
        receivedSize: number;
      }>;
    } | null;
  }>(),
  DeviceCodeTimedOut: {},
  DuplicateComponentIdsError: fields<{
    issues: Array<{
      id: string;
      components: Array<{
        name: string;
        path: string;
        type:
          | 'page'
          | 'plugin'
          | 'menu-plugin'
          | 'modal'
          | 'sidebar-category'
          | 'embedded-script'
          | 'custom-element'
          | 'site-plugin'
          | 'monitoring'
          | 'blocks-widget'
          | 'blocks-widget-panel'
          | 'topology'
          | 'site-component'
          | 'site-component-panel';
      }>;
      suggestedId: string;
    }>;
  }>(),
  FailedToSaveDevelopmentSite: fields<{ path: string }>(),
  FailedToBuildApp: {},
  FailedToBuildAstroApp: {},
  FailedToInitializeGitRepo: {},
  FailedToInstallDependencies: {},
  FailedToCommitChanges: {},
  FailedToDeleteAuthData: {},
  FailedToDeleteLegacyAuthData: {},
  FailedToSaveAuthData: {},
  FailedToSaveApiKeyAuthData: {},
  FailedToRenewAuthTokenSiteNotFound: fields<{ siteId: string }>(),
  FailedToRenewAuthTokenRefreshTokenRevoked: {},
  FailedToLoginWithRefreshToken: {},
  FailedToInstallPackage: fields<{ packageName: string }>(),
  FailedToInstallPackages: {},
  FailedToUninstallPackage: fields<{ packageName: string }>(),
  FailedToRunExternalCommand: fields<{ file: string; args: string[] }>(),
  FailedMigrationToAnyNpm: {},
  FailedToCloneRepo: {},
  AuthenticationRequired: {},
  SiteConfiguredRequired: {},
  NonInteractiveTerminal: {},
  FailedToWatchFiles: {},
  InvalidApiKey: {},
  AccountWasDeleted: {},
  CliAppVersionMismatch: fields<{
    cliVersion: string;
    cliAppVersion: string;
  }>(),
  NetworkError: {},
  FailedToQueryLocales: {},
  FailedToQueryPublishedContents: {},
  NoTranslationPublishedContents: {},
  TranslationUnsupportedForProjectType: {},
  TranslationFileNotFound: fields<{ filePath: string }>(),
  InvalidTranslationsFileSchema: fields<{
    filePath: string;
    zodError: ZodError;
  }>(),
  ConnectUnsupportedForProjectType: {},
  MultilingualAppNotInstalled: {},
  FailedToFetchPrimaryLanguage: {},
  FailedToPushTranslations: {},
  FailedToPullTranslations: {},
  AppForVersionNotFound: fields<{ version: string }>(),
  UnknownServicePlugin: fields<{ name: string }>(),
  InvalidServicePluginFolder: fields<{ relativePath: string }>(),
  FailedToGetLogsTopology: {},
  FailedInstallCloudflareNoAccess: fields<{ path: string }>(),
  CliAppTemplateNotFound: fields<{ id: string }>(),
  LocalTemplatePathNotFound: fields<{ path: string }>(),
  DeniedPermissionToGetAppSecret: {},
  FailedToOpenBrowser: {},
  DeprecatedVersion: fields<{
    currentVersion: string;
    latestVersion?: string;
    deprecated?: string;
    hasCliAppInstalled: boolean;
  }>(),
  InvalidDashboardPageRoute: fields<{ route: string; errorMessage: string }>(),
  InvalidEmbeddedScriptFolder: fields<{
    path: string;
    errorMessage: string;
  }>(),
  InvalidEmbeddedScriptTemplate: fields<{
    templateFilename: string;
    errorMessage: string;
  }>(),
  InvalidCustomElementTagName: fields<{
    tagName: string;
    errorMessage: string;
  }>(),
  InvalidSiteFrontendPackageVersion: fields<{
    packageName: string;
    versionRange: string;
  }>(),
  InvalidServicePluginImplementation: fields<{
    configuredImplementation: string;
  }>(),
  FailedToInitializeSiteFsManager: {},
  FailedToGetSiteFsManagerCode: {},
  FailedToGetSiteFsManagerDocument: {},
  FailedToApplySiteFsManagerFsUpdates: {},
  FailedToApplySiteFsManagerExternalUpdates: {},
  ServeDeprecated: fields<{ packageManagerRunCmd?: string }>(),
  CannotFindConfigReferencedAsset: fields<{
    asset: string;
    configPath: string;
    field: string;
  }>(),
  FailedToCreateIntellisenseAdapter: {},
  FailedToInitIntellisenseAdapterTypes: {},
  FailedToUpdateIntellisenseAdapterTypes: {},
  WebMethodOutsideOfProjectDirectory: fields<{
    relativeId: string;
  }>(),
  FailedToEvalBackendExtension: fields<{
    filename: string;
    errorMessage: string;
  }>(),
  DeploymentPipelineFailed: fields<{
    tasksDescriptions: Array<{
      taskName: string;
      status: 'SUCCESS' | 'ERROR';
      description: Record<string, string>;
    }>;
  }>(),
  InsufficientNonInterractiveReleaseParameters: {},
  AppNameArgumentIsInvalid: fields<{
    errorMessage: string;
  }>(),
  ProjectNameArgumentIsInvalid: fields<{
    errorMessage: string;
  }>(),
  BusinessNameArgumentIsInvalid: fields<{
    errorMessage: string;
  }>(),
  CannotReleaseMinorInNoninteractive: {},
  SiteComponentConfigNotFound: fields<{ configPath: string }>(),
  SiteComponentPanelConfigNotFound: fields<{ configPath: string }>(),
  SiteComponentPanelDoNotExists: fields<{ panelName: string }>(),
  FailedToImportCliApp: {},
  FailedToCleanDistFolder: {},
  FailedToIdentifyProgramFlow: fields<{ reason: string }>(),
  BuildOutputMissing: fields<{ reason: string }>(),
  FailedToCreateMonitoringVitePlugin: fields<{
    configPath: string;
  }>(),
  FailedToSyncVeloCLIServerDocumentUpdate: {},
  AppReleaseConfigViolations: {},
  ProjectIsAlreadyLinked: {},
  ProjectIsNotLinkable: {},
  MissingPresetCssForSiteComponent: fields<{
    componentId: string;
    presetName: string;
    elementBasePath?: string;
  }>(),
  EAccessPermissionDenied: {},
  InvalidSiteComponentElementItem: fields<{
    elementPath: string;
  }>(),
  TrustedAppIsNotMarkedAsInternal: {},
  DevCommandFailed: {},
  VibeRepositorySetupTimedout: {},
});

export const CliErrorCode = variant({
  ...CliSystemErrorCode,
  ...CliUserErrorCode,
});
